import { Add, Search } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { FC, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  bulkPayReceipts,
  getMonths,
  getReicepts,
  searchForReceiptsPerMonth,
} from "services/receipt";
import { EmptyContent } from "./EmptyContent";
import { PaymentModeButton } from "./PaymentModeButton";
import ReceiptsAccordion from "./ReceiptsAccordion";
import { SearchByContainer } from "./SearchByContainerType";

interface Receipt {
  client_id: number | null;
  amount_given: string;
  receipt_number: string;
  amount_received: string;
  containers: { id: number; name: string; type: string }[];
}

interface ReceiptListProps {
  addDay: () => void;
}

export const ReiceptListView: FC<ReceiptListProps> = ({ addDay }) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [includeDays, setIncludeDays] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [receiptNumber, setReceiptNumber] = useState<string | null>(null);
  const [selectedReceipts, setSelectedReceipts] = useState<
    Record<string, Receipt[]>
  >({});
  const [totalAmountPaid, setTotalAmountPaid] = useState<number>(0);

  


  const {
    data: receipts,
    isLoading,
    isError,
  } = useQuery(
    ["receipts", { from: fromDate, to: toDate, summarizeOnly: includeDays }],
    {
      queryFn: () =>
        getReicepts({
          query: {
            from: fromDate,
            to: toDate,
            includeDays: includeDays,
          },
        }),
      enabled: !!fromDate && !!toDate,
    }
  );

  const { data: monthData, isLoading: isMonthLoading } = useQuery(
    "months",
    getMonths
  );

  const { data: receiptsResult, isLoading: isSearchingReceipts } = useQuery(
    ["search-receipts", selectedMonth, receiptNumber],
    () =>
      searchForReceiptsPerMonth({
        month: String(selectedMonth),
        receipt_number: Number(receiptNumber),
      }),
    {
      enabled: !!selectedMonth && !!receiptNumber,
    }
  );

  const formatReceipts = (data: any) => {
    return data.flatMap((receipt: any) =>
      receipt.receipts.map((_receipt: any) => ({
        day: receipt.day,
        receipt_number: _receipt.receipt_number,
        details: _receipt,
      }))
    );
  };

  const queryClient = useQueryClient();

  const formatedReceipts = formatReceipts(receiptsResult?.data || []);

  const handleMonthChange = (event: any) => {
    setSelectedMonth(event.target.value as string);
  };

  const handleSelect = (event: any, value: any) => {
    if (value) {
      const selectedDay = value.day;
      const selectedReceipt = value.details;

      setSelectedReceipts((prev) => ({
        ...prev,
        [selectedDay]: [...(prev[selectedDay] || []), selectedReceipt],
      }));
    }
  };

  const handleDelete = (receiptNumber: string) => {
    setSelectedReceipts((prevReceipts) => {
      const updatedReceipts = { ...prevReceipts };

      Object.keys(updatedReceipts).forEach((day) => {
        updatedReceipts[day] = updatedReceipts[day].filter(
          (receipt) => receipt.receipt_number !== receiptNumber
        );

        if (updatedReceipts[day].length === 0) {
          delete updatedReceipts[day];
        }
      });

      return updatedReceipts;
    });
  };

  const bulkPayMutation = useMutation(bulkPayReceipts, {
    onSuccess: (data) => {
      queryClient.refetchQueries([
        "search-receipts",
        selectedMonth,
        receiptNumber,
      ]);
      setTotalAmountPaid(data?.data?.totalDifference);
    },
    onError: () => {
      console.log("Error");
    },
  });

  const autoPay = () => {
    bulkPayMutation.mutate(selectedReceipts);
  };

  const reset = () => {
    setSelectedReceipts({});
    setTotalAmountPaid(0);
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "white",
          width: "100%",
          p: isMobile ? 1 : 2,
          borderRadius: "0.5rem",
        }}
        my={2}
      >
        <Stack spacing={isMobile ? 2 : 3}>
          {/* Toolbar: Adjust based on screen size */}
          <Toolbar
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
            }}
          >
            <Button
              onClick={addDay}
              size={isMobile ? "medium" : "large"}
              variant="contained"
              startIcon={<Add />}
              fullWidth={isMobile}
            >
              Open new day
            </Button>
            <Button
              color="info"
              size={isMobile ? "medium" : "large"}
              startIcon={<Search />}
              fullWidth={isMobile}
              onClick={() => setDialogOpen(true)}
            >
              Search for day
            </Button>
            <PaymentModeButton from={fromDate} to={toDate} />
          </Toolbar>

          {/* Date Fields and Switch */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            alignItems="center"
            px={2}
            {...(isMobile && { pr: 5 })}
          >
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              fullWidth={isMobile}
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              fullWidth={isMobile}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeDays}
                  onChange={(e) => setIncludeDays(e.target.checked)}
                  name="includeDays"
                />
              }
              label="Include Days"
            />
          </Stack>
        </Stack>

        <SearchByContainer />
      </Box>

      {dialogOpen && (
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          PaperProps={{
            sx: {
              width: 500,
            },
          }}
        >
          <DialogTitle>Search Receipts</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth || ""}
                  onChange={handleMonthChange}
                  label="Month"
                >
                  {monthData?.data?.map((month: any) => (
                    <MenuItem key={month.month} value={month.month}>
                      {month.month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                freeSolo
                options={formatedReceipts?.filter(
                  (receipt: any) =>
                    !Object.keys(selectedReceipts)
                      .flatMap((day) => selectedReceipts[day])
                      .some(
                        (selectedReceipt) =>
                          selectedReceipt.receipt_number ===
                          receipt.details.receipt_number
                      )
                )}
                getOptionLabel={(option: any) =>
                  `${option.day} - ${option.receipt_number}`
                }
                renderOption={(props, option: any) => {
                  const day = option?.day || "Unknown Day";
                  const receiptNumber =
                    option?.details?.receipt_number || "N/A";
                  const amountReceived =
                    option?.details?.amount_received || "0";
                  const amountGiven = option?.details?.amount_given || "0";
                  const lostAmount = (
                    Number(amountReceived) - Number(amountGiven)
                  ).toFixed(2);

                  const isLoss = Number(lostAmount) > 0; // Use red color for negative lost amount

                  return (
                    <li {...props}>
                      <Typography
                        variant="body1"
                        component="span"
                        sx={{ mr: 1 }}
                      >
                        {day} | #{receiptNumber} | AR. {amountReceived} | AG.
                        {amountGiven} |
                      </Typography>
                      <Typography
                        variant="body1"
                        component="span"
                        sx={{
                          fontWeight: "bold",
                          color: isLoss ? "red" : "green", // Red for loss, green for gain
                        }}
                      >
                        LOST: {lostAmount}
                      </Typography>
                    </li>
                  );
                }}
                inputMode="search"
                onChange={handleSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Receipt Number"
                    onChange={(e) => setReceiptNumber(e.target.value)}
                    type="number"
                    sx={{
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                        {
                          display: "none",
                        },
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                    }}
                  />
                )}
              />

              <Box
                sx={{
                  disaplay: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {(Object.keys(selectedReceipts) || [])?.map(
                  (item: any, index: any) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        backgroundColor: "#1A4870",
                        borderRadius: "0.5rem",
                        my: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: "white", fontWeight: "bold", mb: 2 }}
                      >
                        {item}
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ color: "#F9DBBA", mb: 1.5, fontWeight: 500 }}>
                          Receipts:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mb: 2
                          }}
                        >
                          {selectedReceipts[item]?.map((receipt: any) => (
                            <Chip
                              key={receipt.receipt_number}
                              variant="filled"
                              label={
                                <Box sx={{ py: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>#{receipt.receipt_number}</Typography>
                                  <Typography variant="body2">{(receipt?.amount_received - receipt?.amount_given).toLocaleString()} TND</Typography>
                                </Box>
                              }
                              sx={{
                                px: 1,
                                color: "#F9DBBA",
                                fontSize: "1rem",
                                fontWeight: "bold",
                                backgroundColor: "#1F316F",
                                height: 'auto',
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                }
                              }}
                              onDelete={() =>
                                handleDelete(receipt.receipt_number)
                              }
                            />
                          ))}
                        </Box>
                        
                        <Typography variant="subtitle1" sx={{ color: "#F9DBBA", mb: 1.5, fontWeight: 500 }}>
                          Containers:
                        </Typography>
                        
                        {/* Get all unique containers across all receipts for this item */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          maxWidth: '100%'
                        }}>
                          {/* Extract and deduplicate containers */}
                          {Array.from(new Set(
                            selectedReceipts[item]
                              ?.flatMap((receipt: any) => receipt?.containers || [])
                              ?.map((container: any) => ({
                                containerName: container?.name,
                                quantity: container?.quantity,
                              }))
                              ?.filter(Boolean)
                          )).map((container: any, idx: number) => (
                            <Chip
                              key={idx}
                              label={<Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{container.containerName}</Typography>
                              {container.quantity ?   <Typography variant="body2">x{container.quantity}</Typography> : null}
                              </Stack>}
                                sx={{
                                backgroundColor: '#2C5282',
                                color: 'white',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: '#3182CE'
                                }
                              }}
                              icon={<Box 
                                component="span" 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: '#4FD1C5',
                                  mr: 1
                                }}
                              />}
                            />
                          ))}
                          
                          {/* Show message if no containers */}
                          {!selectedReceipts[item]?.some((receipt: any) => 
                            receipt?.containers?.length > 0
                          ) && (
                            <Typography variant="body2" sx={{ color: '#A0AEC0', fontStyle: 'italic' }}>
                              No containers associated with these receipts
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )
                )}
              </Box>
            </Stack>
            {totalAmountPaid > 0 && (
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  color: "#5B99C2 ",
                  textAlign: "right",
                }}
              >
                You pay <i style={{ color: "#1A4870" }}>{totalAmountPaid}</i> DT
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDialogOpen(false);
                setSelectedReceipts({});
              }}
            >
              Cancel
            </Button>
            <Button onClick={autoPay}>PAY NOW</Button>
            {bulkPayMutation.isSuccess && (
              <Button onClick={reset}>RESET</Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {isLoading ||
        isMonthLoading ||
        (isSearchingReceipts && <CircularProgress />)}
      {isError && <Alert severity="error">Failed to load receipts data.</Alert>}
      {receipts && <ReceiptsAccordion data={receipts.data} error={isError} />}
      {!isLoading && !isError && (!receipts || receipts.data.length === 0) && (
        <EmptyContent />
      )}
    </>
  );
};
